import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'

import CardAdFavHeart from './card-ad-fav-heart.js'

const NO_OP = () => {}

const useAddFavorite = ({adId}) => {
  const [isLoginFlowCancelled, setIsLoginFlowCancelled] = useState(false)
  const [isAddedToFavorites, setIsAddedToFavorites] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const {domain} = useContext(Context)
  const {triggerLoginModal} = useContext(LoginModalContext)

  const config = domain.get('config')
  const {LOGIN_SOURCE_ID_ADD_FAV} = config.get('login')
  const {HIT_ORIGINS} = config.get('tracking')

  useEffect(() => {
    showLoginModal &&
      triggerLoginModal({
        modalProps: {
          source: LOGIN_SOURCE_ID_ADD_FAV,
          trackingOrigin: HIT_ORIGINS.AD_FAVORITED,
          onLogin: () => {
            domain
              .get('add_user_favorites_use_case')
              .execute({
                adId
              })
              .then(([, response]) => {
                response && setIsAddedToFavorites(true)
              })
          },
          onClose: () => {
            setIsLoginFlowCancelled(true)
            setShowLoginModal(false)
          }
        }
      })
  }, [showLoginModal, adId])

  return {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal, showLoginModal}
}

const CardAdFav = ({ad, extendedTrackingData, isMakedAsFavorite, onToggleFav = NO_OP, trackingOrigin}) => {
  const [isFavorite, toggleFavorite] = useState(ad.isFavorite)

  const {domain} = useContext(Context)

  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {isAddedToFavorites, isLoginFlowCancelled, setShowLoginModal} = useAddFavorite({adId: ad.id})
  const delayToNextFrame = useDelayToNextFrame()

  const {track} = useAnalyticsTrack()
  const {track: criteoEventTrack} = useCriteoTrack()

  const {id: adId, isFavorite: isFaved} = ad

  useEffect(() => {
    toggleFavorite(isFaved)
  }, [isFaved])

  useEffect(() => {
    toggleFavorite(isMakedAsFavorite)
  }, [isMakedAsFavorite])

  useEffect(() => {
    if (isAddedToFavorites) {
      toggleFavorite(true)
      delayedTracking()
    }
  }, [isAddedToFavorites, isLoginFlowCancelled])

  const delayedTracking = () => {
    delayToNextFrame(() => {
      domain
        .get('get_event_tracking_data_use_case')
        .execute({
          ad,
          event: isFavorite ? TRACKING_EVENT_NAMES.AD_UNFAVORITED : TRACKING_EVENT_NAMES.AD_FAVORITED,
          extendedTrackingData,
          origin: trackingOrigin
        })
        .then(trackingData => track(trackingData))

      !isFavorite &&
        criteoEventTrack({
          event: criteoTrackEvents.ADD_TO_CART,
          eventData: {
            adId
          }
        })
    })
  }

  const handleToggleFavorite = ev => {
    ev.preventDefault()
    ev.stopPropagation()

    const nextState = !isFavorite

    const handleFavUseCase = isFavorite ? 'delete_user_favorites_use_case' : 'add_user_favorites_use_case'

    domain
      .get(handleFavUseCase)
      .execute({adId, filteredProvinces: extendedTrackingData.provinces})
      .then(([favoriteError]) => {
        if (favoriteError instanceof USER_NOT_LOGGED_IN_USER_ERROR) {
          return setShowLoginModal(true)
        }
        if (!favoriteError) {
          onToggleFav(nextState)
          delayedTracking()
        }
      })
  }

  return <CardAdFavHeart adTitle={ad.title} isFavorite={isFavorite} onClick={handleToggleFavorite} />
}

CardAdFav.displayName = 'CardAdFav'

CardAdFav.propTypes = {
  ad: PropTypes.object.isRequired,
  extendedTrackingData: PropTypes.object,
  isMakedAsFavorite: PropTypes.bool,
  onToggleFav: PropTypes.func,
  trackingOrigin: PropTypes.string
}

export default CardAdFav
